/**
 * Cabinet document-formatting settings (`cabinet_settings.formatting_settings` jsonb).
 *
 * This is a faithful mirror of `apps/addin-word/src/types/cabinet.ts` (the Word
 * add-in's Style Editor contract) — NOT re-exported from `@hector/shared-types`
 * because that package has no `FormattingSettings` entry today (verified by grep
 * before writing this file, Task 4 brief Étape 0).
 *
 * WHY a full mirror and not a stripped-down v1 subset: the backend PATCH
 * `/cabinet-settings/:cabinetId/formatting` merges with a PostgreSQL root-level
 * `||` jsonb operator (`CabinetSettingRepository.updateFormattingSettings`) —
 * this only merges TOP-LEVEL keys. Sending `{ headings: { h1: {...} } }` would
 * silently REPLACE the whole `headings` object and wipe h2-h6. The add-in's own
 * `cabinetService.updateFormattingSettings` comment states this explicitly and
 * always sends the COMPLETE `FormattingSettings` object — the web port does the
 * same (see `useFormattingSettings.ts`). Keeping the full type (not just the v1
 * editable subset) lets the editor round-trip `markers`/`quotes`/`titleBox`/
 * `pageBreaks`/`pieceCitationFormat` untouched even though the v1 UI never
 * renders them.
 */

/** Text alignment options */
export type TextAlignment = 'left' | 'center' | 'right' | 'justified';

/** Underline style options */
export type UnderlineStyle = 'none' | 'single' | 'double' | 'dotted';

/** Heading style configuration for a single heading level (H1-H6) */
export interface HeadingStyle {
  fontSize: number;
  bold: boolean;
  italic: boolean;
  underline: UnderlineStyle;
  alignment: TextAlignment;
  spaceBefore: number;
  spaceAfter: number;
  // Optional (Word-style exhaustiveness 2026-07-12) — absent = préserve la charte.
  // 1:1 mirror of the add-in `apps/addin-word/src/types/cabinet.ts`.
  fontName?: string;
  color?: string;      // hex "#RRGGBB"
  leftIndent?: number; // points
  rightIndent?: number;
  /**
   * Per-heading « Encadré » (box) — render this heading inside a bordered box in
   * the generated Word document. Reuses the {@link TitleBoxSettings} shape (DRY).
   * Absent = no box. Meant for H2/H3/H4 only: H1 already uses the GLOBAL
   * {@link FormattingSettings.titleBox}, so boxing it here too would double-box it.
   */
  box?: TitleBoxSettings;
}

/** All heading styles configuration (H1-H6). Only H1-H4 are editable in v1 (see FormattingEditor). */
export interface HeadingStyles {
  h1: HeadingStyle;
  h2: HeadingStyle;
  h3: HeadingStyle;
  h4: HeadingStyle;
  h5: HeadingStyle;
  h6: HeadingStyle;
}

/** Heading levels exposed by the v1 web editor (parity boundary — see task brief). */
export type EditableHeadingLevel = 'h1' | 'h2' | 'h3' | 'h4';

/** Paragraph formatting settings — fully editable in v1. */
export interface ParagraphSettings {
  fontSize: number;
  lineSpacing: number;
  spaceAfter: number;
  spaceAfterReduced: number;
  alignment: TextAlignment;
  firstLineIndent: number;
  // Optional (Word-style exhaustiveness 2026-07-12) — absent = préserve la charte.
  fontName?: string;
  color?: string;      // hex "#RRGGBB"
  leftIndent?: number; // points
  rightIndent?: number;
}

/** Document marker formatting (PLAISE, POUR, CONTRE, pieces, etc.) — not fully editable in v1. */
export interface MarkerStyle {
  bold: boolean;
  italic: boolean;
  underline: UnderlineStyle;
  alignment: TextAlignment;
  lineBreakBefore: boolean;
  lineBreakAfter: boolean;
  fontSize?: number;
  spaceBefore?: number;
  spaceAfter?: number;
}

/** Document markers configuration. Only `pieceBeforeBordereau`/`pieceAfterBordereau` are editable in v1. */
export interface MarkerStyles {
  plaise: MarkerStyle;
  pour: MarkerStyle;
  contre: MarkerStyle;
  devant: MarkerStyle;
  objet: MarkerStyle;
  bordereau: MarkerStyle;
  pieceBeforeBordereau: MarkerStyle;
  pieceAfterBordereau: MarkerStyle;
  demanderesse: MarkerStyle;
  defenderesse: MarkerStyle;
  parCesMotifs: MarkerStyle;
  tresImportant: MarkerStyle;
  /**
   * Act-delimiter classes (2026-07-29) — the two editable styles the hard-coded
   * assignation/commandement preamble formulas collapse into, so a cabinet can
   * restyle « à la requête de », « demeurant », « total dû », etc. in one place.
   *   - `delimiterUnderline`: the bold+underline+left formulas (À LA REQUÊTE DE,
   *     ASSISTÉ(E) DE, DÉCISION ATTAQUÉE, Objet de la demande…).
   *   - `delimiterBold`: the bold-only+left formulas (DEMEURANT, J'ai, TOTAL DÛ,
   *     Ayant pour Avocat…).
   */
  delimiterUnderline: MarkerStyle;
  delimiterBold: MarkerStyle;
}

/**
 * The FULL set of special-document markers exposed by the styles editor (2026-07-25,
 * extended to the two act-delimiter classes 2026-07-29).
 *
 * These are the 14 legal-document delimiters that carry a `MarkerStyle` in the cabinet
 * contract and whose formatting is detected + applied by the Word add-in
 * (`apps/addin-word/src/services/formattingRules.ts` detectors →
 * `insertion/styleResolver.ts` → `hectorStyleDefinitions.ts` markerConfigKey). The web
 * "Charte graphique" editor now exposes them ALL (was: the 2 piece markers only).
 *
 * ⚠️ Word-application caveat (pre-existing): in `hectorStyleDefinitions.ts`, `pour`
 * drives the shared `Hector-Marqueur-BU` style, so `contre`/`devant` currently ride the
 * same Word style — editing those two siblings round-trips in the contract but does not
 * yet materialise as a distinct Word style. (`defenderesse` used to share
 * `Hector-Party-Label` with `demanderesse`; since 2026-07-29 it drives its own
 * `Hector-Defenderesse` style and is honoured independently in generation and
 * reformulation.)
 */
export const EDITABLE_MARKER_KEYS = [
  'plaise', 'pour', 'contre', 'devant', 'objet', 'parCesMotifs', 'tresImportant',
  'bordereau', 'demanderesse', 'defenderesse', 'pieceBeforeBordereau', 'pieceAfterBordereau',
  'delimiterUnderline', 'delimiterBold',
] as const;

/** Union of every marker key the styles editor can edit — a subset of `keyof MarkerStyles`. */
export type EditableMarkerKey = (typeof EDITABLE_MARKER_KEYS)[number];

/** Human-readable French labels for each editable marker (shared by editor + docs). */
export const MARKER_LABELS: Record<EditableMarkerKey, string> = {
  plaise: 'PLAISE',
  pour: 'POUR',
  contre: 'CONTRE',
  devant: 'DEVANT',
  objet: 'OBJET',
  parCesMotifs: 'PAR CES MOTIFS',
  tresImportant: 'TRÈS IMPORTANT',
  bordereau: 'BORDEREAU',
  demanderesse: 'Demanderesse',
  defenderesse: 'Défenderesse',
  pieceBeforeBordereau: 'Pièce (corps)',
  pieceAfterBordereau: 'Pièce (bordereau)',
  delimiterUnderline: 'Marqueur souligné',
  delimiterBold: 'Marqueur gras',
};

/**
 * Union of all style identifiers editable in the split-panel.
 *
 * Defined here — NOT in `FormattingEditor.tsx` — so `StylePreview.tsx` can import
 * it without creating a circular dependency: `FormattingEditor` imports `StylePreview`,
 * so `StylePreview` must never import back from `FormattingEditor`.
 *
 * Note: `actPersonalizationApi.ts` defines a structurally identical `FormattingStyleKey`
 * (pre-existing duplication, tracked as a future consolidation).
 */
export type StyleKey = EditableHeadingLevel | 'normal' | EditableMarkerKey;

/**
 * Human-readable French label for each {@link StyleKey}.
 *
 * Shared by `FormattingEditor` (panel heading, nav row labels) and `StylePreview`
 * (aria-labels on clickable preview blocks). Kept here — single source of truth —
 * so editing a marker label in {@link MARKER_LABELS} propagates to both consumers
 * automatically.
 */
export const STYLE_LABEL: Record<StyleKey, string> = {
  h1: 'Titre 1',
  h2: 'Titre 2',
  h3: 'Titre 3',
  h4: 'Titre 4',
  normal: 'Paragraphe',
  ...MARKER_LABELS,
};

/** Quote formatting settings — out of v1 scope, preserved untouched. */
export interface QuoteSettings {
  italic: boolean;
  quoteStyle: 'french' | 'english' | 'german';
}

/** Title box styling (H1 boxed titles) — out of v1 scope, preserved untouched. */
export interface TitleBoxSettings {
  enabled: boolean;
  borderStyle: 'none' | 'single' | 'double';
  borderWidth: number;
  cellPadding: number;
}

/** Page break rules — out of v1 scope, preserved untouched. */
export interface PageBreakSettings {
  beforePlaise: boolean;
  beforeBordereau: boolean;
  beforeRomanSection: boolean;
}

/** Piece citation format in LLM-generated acts — out of v1 scope, preserved untouched. */
export type PieceCitationFormat = 'piece' | 'prod' | 'nu';

/**
 * Cabinet-configurable page numbering for generated acts.
 *
 * The add-in materialises this as a `PAGE` (+ optional `NUMPAGES`) field inside the
 * section footer (`buildPageNumberOoxml` → `applyPageNumberingToFooter`). Footnotes are
 * OUT OF SCOPE — the generation pipeline never emits footnotes; only the Word Édition
 * agent touches footnotes a lawyer typed. This is page-numbering ONLY.
 *
 * Byte-identical mirror of `apps/addin-word/src/types/cabinet.ts`.
 */
export interface PageNumberingSettings { enabled: boolean; position: 'footer-center' | 'footer-right'; format: 'page-x' | 'x-of-n' | 'x'; }

/** Complete formatting configuration for a cabinet (mirrors the add-in contract 1:1). */
export interface FormattingSettings {
  fontName: string;
  paragraph: ParagraphSettings;
  headings: HeadingStyles;
  markers: MarkerStyles;
  quotes: QuoteSettings;
  titleBox: TitleBoxSettings;
  pageBreaks: PageBreakSettings;
  pieceCitationFormat?: PieceCitationFormat;
  pageNumbering?: PageNumberingSettings;
}

/** Shape of the GET `/cabinet-settings/:cabinetId` response relevant to formatting. */
export interface CabinetFormattingSettingsResponse {
  id: string;
  cabinet_id: string;
  formatting_settings?: FormattingSettings | null;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// DEFAULTS — copied from apps/addin-word/src/types/cabinet.ts so a cabinet that
// never touched the Word Style Editor gets the exact same starting point here.
// ============================================================================

export const DEFAULT_FONT_NAME = 'Garamond';

export const DEFAULT_HEADING_STYLES: HeadingStyles = {
  h1: { fontSize: 14, bold: true, italic: false, underline: 'none', alignment: 'center', spaceBefore: 10, spaceAfter: 8 },
  h2: { fontSize: 14, bold: true, italic: false, underline: 'single', alignment: 'justified', spaceBefore: 8, spaceAfter: 8 },
  h3: { fontSize: 12, bold: true, italic: false, underline: 'none', alignment: 'justified', spaceBefore: 8, spaceAfter: 6 },
  h4: { fontSize: 11, bold: true, italic: false, underline: 'none', alignment: 'justified', spaceBefore: 8, spaceAfter: 6 },
  h5: { fontSize: 11, bold: true, italic: false, underline: 'none', alignment: 'justified', spaceBefore: 6, spaceAfter: 6 },
  h6: { fontSize: 11, bold: true, italic: false, underline: 'none', alignment: 'justified', spaceBefore: 6, spaceAfter: 6 },
};

export const DEFAULT_PARAGRAPH_SETTINGS: ParagraphSettings = {
  fontSize: 11,
  // Must stay equal to the add-in's `DEFAULT_PARAGRAPH_SETTINGS.lineSpacing`
  // (`apps/addin-word/src/types/cabinet.ts`). This editor merges the stored charte over
  // these defaults and SAVES the merged object back, so a divergent value here would be
  // persisted on the first save and would silently override the add-in default forever.
  lineSpacing: 1.15,
  spaceAfter: 6,
  spaceAfterReduced: 0,
  alignment: 'justified',
  firstLineIndent: 0,
};

export const DEFAULT_MARKER_STYLES: MarkerStyles = {
  plaise: { bold: true, italic: false, underline: 'none', alignment: 'center', lineBreakBefore: false, lineBreakAfter: true },
  pour: { bold: true, italic: false, underline: 'single', alignment: 'left', lineBreakBefore: true, lineBreakAfter: true },
  contre: { bold: true, italic: false, underline: 'single', alignment: 'left', lineBreakBefore: true, lineBreakAfter: true },
  devant: { bold: true, italic: false, underline: 'single', alignment: 'left', lineBreakBefore: true, lineBreakAfter: true },
  objet: { bold: true, italic: false, underline: 'none', alignment: 'left', lineBreakBefore: false, lineBreakAfter: true },
  bordereau: { bold: true, italic: false, underline: 'none', alignment: 'left', lineBreakBefore: false, lineBreakAfter: false, fontSize: 11, spaceBefore: 0, spaceAfter: 6 },
  pieceBeforeBordereau: { bold: true, italic: false, underline: 'none', alignment: 'right', lineBreakBefore: true, lineBreakAfter: true },
  pieceAfterBordereau: { bold: false, italic: false, underline: 'none', alignment: 'left', lineBreakBefore: false, lineBreakAfter: false, fontSize: 11 },
  demanderesse: { bold: true, italic: true, underline: 'none', alignment: 'right', lineBreakBefore: true, lineBreakAfter: true },
  defenderesse: { bold: true, italic: true, underline: 'none', alignment: 'right', lineBreakBefore: true, lineBreakAfter: true },
  parCesMotifs: { bold: true, italic: false, underline: 'none', alignment: 'center', lineBreakBefore: true, lineBreakAfter: true, fontSize: 12, spaceBefore: 12, spaceAfter: 12 },
  tresImportant: { bold: true, italic: false, underline: 'single', alignment: 'left', lineBreakBefore: true, lineBreakAfter: true },
  // Act-delimiter classes (2026-07-29). delimiterUnderline mirrors the add-in's C1
  // bold+underline+left formulas; delimiterBold mirrors the C2 bold-only+left formulas.
  delimiterUnderline: { bold: true, italic: false, underline: 'single', alignment: 'left', lineBreakBefore: true, lineBreakAfter: true },
  delimiterBold: { bold: true, italic: false, underline: 'none', alignment: 'left', lineBreakBefore: false, lineBreakAfter: true },
};

export const DEFAULT_QUOTE_SETTINGS: QuoteSettings = { italic: true, quoteStyle: 'french' };

export const DEFAULT_TITLE_BOX_SETTINGS: TitleBoxSettings = { enabled: true, borderStyle: 'single', borderWidth: 1, cellPadding: 5 };

export const DEFAULT_PAGE_BREAK_SETTINGS: PageBreakSettings = { beforePlaise: true, beforeBordereau: true, beforeRomanSection: true };

// Default `enabled: false` — opt-in. A cabinet that never touched this must not suddenly
// get a page-number footer on its generated acts.
export const DEFAULT_PAGE_NUMBERING_SETTINGS: PageNumberingSettings = { enabled: false, position: 'footer-center', format: 'page-x' };

export const DEFAULT_FORMATTING_SETTINGS: FormattingSettings = {
  fontName: DEFAULT_FONT_NAME,
  paragraph: DEFAULT_PARAGRAPH_SETTINGS,
  headings: DEFAULT_HEADING_STYLES,
  markers: DEFAULT_MARKER_STYLES,
  quotes: DEFAULT_QUOTE_SETTINGS,
  titleBox: DEFAULT_TITLE_BOX_SETTINGS,
  pageBreaks: DEFAULT_PAGE_BREAK_SETTINGS,
  pageNumbering: DEFAULT_PAGE_NUMBERING_SETTINGS,
};

// The « Police globale » font catalog lives in `src/data/microsoftFonts.ts`
// (consumed by `src/components/personnalisation/FontPicker.tsx`).
